import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Select from '../../common/Select';
import Button from '../../common/Button';
import { validateRequired, validateEmail, validatePhone } from '../../../utils/validators';
import engineerService from '../../../services/engineerService';
import managerService from '../../../services/managerService';
import { mockTeamLeads } from '../../../data/mockData';

const EditEngineerModal = ({ isOpen, onClose, onSubmit, engineer }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    designation: '',
    team_lead_id: '',
    is_active: true,
    password: '',
    is_locked_out: false
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [teamLeads, setTeamLeads] = useState(mockTeamLeads || []); 
  const [loadingTLs, setLoadingTLs] = useState(false); 
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTeamLeads();
      loadManagers();
    }
  }, [isOpen, teamLeads.length]);

  const loadManagers = async () => {
    setLoadingManagers(true);
    const result = await managerService.getAllManagers();
    if (result.success) setManagers(result.data);
    setLoadingManagers(false);
  };

  useEffect(() => {
    if (engineer) {
      setFormData({
        name: engineer.name || '',
        email: engineer.email || '',
        contact: engineer.contact || '',
        designation: engineer.designation || '',
        team_lead_id: engineer.team_lead_id?.toString() || '',
        is_active: engineer.is_active !== undefined ? engineer.is_active : true,
        password: '', // Kept empty so it's only updated if the admin types a new one
        is_locked_out: engineer.is_locked_out !== undefined ? engineer.is_locked_out : false
      });
    }
  }, [engineer]);

  const loadTeamLeads = async () => {
    setLoadingTLs(true);
    try {
      const result = await engineerService.getAllTeamLeads();
      console.log('Team Leads Result:', result);
      if (result.success) {
        console.log('Team Leads Data:', result.data);
        setTeamLeads(result.data || []);
      } else {
        console.error('Failed to load team leads:', result.error);
        setTeamLeads([]);
      }
    } catch (err) {
      console.error('Error loading team leads:', err);
      setTeamLeads([]);
    } finally {
      setLoadingTLs(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!validateRequired(formData.name)) {
      newErrors.name = 'Name is required';
    }
    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!validateRequired(formData.contact)) {
      newErrors.contact = 'Contact number is required';
    } else if (!validatePhone(formData.contact)) {
      newErrors.contact = 'Please enter a valid 10-digit phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    setSubmitting(true);

    try {
      const updateData = {
        ...formData,
        team_lead_id: formData.team_lead_id ? parseInt(formData.team_lead_id) : null
      };

      // Only send password if a new one is provided
      if (!updateData.password) {
        delete updateData.password;
      }

      // First update basic info
      const result = await onSubmit(engineer.id, updateData);
      
      if (result.success) {
        // Then update TL if changed
        if (engineer.team_lead_id !== updateData.team_lead_id) {
          await engineerService.updateEngineerTL(engineer.id, updateData.team_lead_id);
        }
        
        // success message is usually handled by parent but we can do it here if needed
        // success('Engineer updated successfully!');
        onClose(true); // Signal refresh
      }
    } catch (error) {
      console.error('Error updating engineer:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setErrors({});
      onClose();
    }
  };

  const teamLeadOptions = [
    { value: '', label: '-- No Supervisor --' },
    ...teamLeads.map(tl => ({
      value: tl.id.toString(),
      label: `${tl.name} (TL)`
    })),
    ...managers.map(m => ({
      value: m.id.toString(),
      label: `${m.name} (Manager)`
    }))
  ];

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Updating...' : 'Update Engineer'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Edit Engineer - ${engineer?.name}`}
      size="medium"
      footer={footer}
    >
      <form onSubmit={handleSubmit}>
        <Input
          label="Full Name"
          name="name"
          placeholder="e.g., John Smith"
          value={formData.name}
          onChange={handleChange}
          error={errors.name}
          required
        />
        <Input
          label="Designation"
          name="designation"
          placeholder="e.g., Field Engineer"
          value={formData.designation}
          onChange={handleChange}
          error={errors.designation}
        />

        <div style={gridStyle}>
          <Input
            label="Email"
            name="email"
            type="email"
            placeholder="engineer@amc.com"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            required
          />

          <Input
            label="Phone Number"
            name="contact"
            placeholder="9876543210"
            value={formData.contact}
            onChange={handleChange}
            error={errors.contact}
            required
          />
        </div>

        <div style={gridStyle}>
          <Select
            label="Team Lead"
            name="team_lead_id"
            value={formData.team_lead_id}
            onChange={handleChange}
            options={teamLeadOptions}
            disabled={loadingTLs}
          />

          <Input
            label="New Password"
            name="password"
            type="password"
            canPeekPassword
            placeholder="Leave blank to keep unchanged"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
          />
        </div>


        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px',
          background: formData.is_locked_out ? '#fee2e2' : 'var(--surface-light)',
          border: formData.is_locked_out ? '1px solid #ef4444' : 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          marginTop: '8px'
        }}>
           <input
            type="checkbox"
            name="is_locked_out"
            checked={formData.is_locked_out}
            onChange={handleChange}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span style={{ fontSize: '14px', color: formData.is_locked_out ? '#ef4444' : 'var(--text-primary)', fontWeight: '500' }}>
            Account Locked (Uncheck to reinstate access)
          </span>
        </label>

        {engineer?.active_tickets > 0 && (
          <div style={{
            padding: '12px 16px',
            background: '#f59e0b20',
            border: '1px solid #f59e0b',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#f59e0b',
            marginTop: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
             This engineer currently has {engineer.active_tickets} active ticket(s).
          </div>
        )}
      </form>
    </Modal>
  );
};

export default EditEngineerModal;